import type { JSX } from 'react';
import { PeekCard, type PeekCardProps } from './PeekCard.js';
import { SheetEntityView, type SheetEntityViewProps } from './SheetEntityView.js';
import { JournalEntityView, type JournalEntityViewProps } from './JournalEntityView.js';
import { PageEntityView, type PageEntityViewProps } from './PageEntityView.js';

export type EntityKind = 'sheet' | 'journal' | 'page';
export type EntityLevel = 'peek' | 'pane' | 'full';

interface SheetEntityViewParams extends Omit<SheetEntityViewProps, 'sheetId' | 'level'> {
  readonly kind: 'sheet';
  readonly id: string;
  readonly level: EntityLevel;
  readonly peek?: Omit<PeekCardProps, 'children'>;
}

interface JournalEntityViewParams extends Omit<JournalEntityViewProps, 'entryId' | 'level'> {
  readonly kind: 'journal';
  readonly id: string;
  readonly level: Exclude<EntityLevel, 'peek'>;
}

// extends Omit<PageEntityViewProps, never>: optional pageData?/draftPending?
// from PageEntityViewProps are inherited automatically (T004).
interface PageEntityViewParams extends Omit<PageEntityViewProps, never> {
  readonly kind: 'page';
  readonly id: string;
  readonly level: Exclude<EntityLevel, 'peek'>;
}

export type EntityViewProps =
  | SheetEntityViewParams
  | JournalEntityViewParams
  | PageEntityViewParams;

export function EntityView(props: EntityViewProps): JSX.Element | null {
  switch (props.kind) {
    // dispatcher: kind x level -> sibling subcomponent (T009-T012)
    case 'sheet': {
      const { kind: _kind, id, level, peek, ...passthrough } = props;
      void _kind;
      if (level === 'peek') {
        if (peek === undefined) {
          return null;
        }
        return (
          <PeekCard {...peek}>
            <SheetEntityView sheetId={id} level="pane" {...passthrough} />
          </PeekCard>
        );
      }
      return <SheetEntityView sheetId={id} level={level} {...passthrough} />;
    }
    case 'journal': {
      const { kind: _kind, id, level, ...passthrough } = props;
      void _kind;
      return <JournalEntityView entryId={id} level={level} {...passthrough} />;
    }
    case 'page': {
      // passthrough carries pageId/pageName/tokenCount/pageData/draftPending/
      // onExitStudio; only kind/id/level are excluded, so pageData and
      // draftPending reach PageEntityView via spread (T006).
      const { kind: _kind, id: _id, level: _level, ...passthrough } = props;
      void _kind;
      void _id;
      void _level;
      return <PageEntityView {...passthrough} />;
    }
    default:
      return null;
  }
}

export default EntityView;
